import { useSelector } from 'react-redux';
import { canUserSelector } from '@store/common/selectors';

/**
 * Central per-user permission accessor (map ticket T7 / ISSUE-4).
 *
 * Reads the merged permission map on the logged-in user's own profile (the server merges KV
 * permissions such as `hasViewerAccess` into the profile — see the loadProfile middleware) and
 * returns whether the current user has the given permission. This is the single client-side
 * `canUser()` accessor the registry model needs (the gap T1 flagged).
 *
 * Fail-closed: an absent / falsy permission returns false.
 *
 * Usage:
 *   const hasViewerAccess = useCanUser('hasViewerAccess');
 *
 * Composition note: to gate a UI section that is rendered through the existing
 * `<AccessWrapper feature=...>` / `useCanAccessFeature` seam (role/account-based), combine that
 * gate with `useCanUser(...)`. That seam is the natural extension point for a future section whose
 * render path goes through AccessWrapper; this tracer (the Sessions tab) is gated directly with
 * `useCanUser` because it is built as a tab-config entry, not wrapped in AccessWrapper.
 */
function useCanUser(permissionKey: string): boolean {
  return useSelector((state) => canUserSelector(state, permissionKey));
}

export default useCanUser;
